/**
 * Find Your Ritual - Interactive Kit Finder Quiz
 * Shopify Online Store 2.0
 *
 * CONFIGURATION:
 * Kit URLs and names are configured in the Shopify Theme Editor.
 * Navigate to: Theme Customizer > Add Section > Find Your Ritual
 * Then configure the 7 kit URLs and names in the section settings.
 *
 * FEATURES:
 * - 4-question interactive quiz with scoring logic
 * - Optional email capture step (non-blocking)
 * - LocalStorage persistence
 * - Accessibility features (keyboard nav, ARIA labels, focus management)
 * - Tie-break rule: lowest kit number wins
 */

(function() {
  'use strict';

  // Quiz questions and scoring configuration
  const QUIZ_DATA = {
    questions: [
      {
        id: "q1",
        text: "How have you been feeling lately?",
        subtitle: "Select up to 2 options",
        maxSelections: 2,
        options: [
          {
            id: "q1_a",
            text: "I feel like my mind is racing and disorganized",
            kits: [1]
          },
          {
            id: "q1_b",
            text: "I feel like I always have something pending or difficulty finishing tasks",
            kits: [5]
          },
          {
            id: "q1_c",
            text: "I feel tired even when I rest",
            kits: [2, 3]
          },
          {
            id: "q1_d",
            text: "I've been living on autopilot",
            kits: [4]
          },
          {
            id: "q1_e",
            text: "I have difficulty closing chapters in my life",
            kits: [5]
          },
          {
            id: "q1_f",
            text: "I've been putting myself second",
            kits: [4, 6]
          },
          {
            id: "q1_g",
            text: "I feel good, but disconnected from what matters",
            kits: [7, 4]
          }
        ]
      },
      {
        id: "q2",
        text: "What weighs on you the most today?",
        subtitle: "Select 1 option",
        maxSelections: 1,
        options: [
          {
            id: "q2_a",
            text: "Excessive thoughts",
            kits: [1]
          },
          {
            id: "q2_b",
            text: "Lack of energy",
            kits: [2]
          },
          {
            id: "q2_c",
            text: "Lack of boundaries",
            kits: [3]
          },
          {
            id: "q2_e",
            text: "Unresolved situations",
            kits: [5]
          },
          {
            id: "q2_f",
            text: "Feeling stuck",
            kits: [4]
          },
          {
            id: "q2_g",
            text: "Just wanting to reconnect",
            kits: [7]
          }
        ]
      },
      {
        id: "q3",
        text: "What does your body need right now?",
        subtitle: "Select up to 2 options",
        maxSelections: 2,
        options: [
          {
            id: "q3_a",
            text: "Deep rest",
            kits: [1, 2]
          },
          {
            id: "q3_b",
            text: "Care and support",
            kits: [3, 4, 6]
          },
          {
            id: "q3_c",
            text: "Silence",
            kits: [2, 7]
          },
          {
            id: "q3_d",
            text: "Gentle movement",
            kits: [7]
          },
          {
            id: "q3_e",
            text: "Stable energy",
            kits: [2, 7]
          },
          {
            id: "q3_f",
            text: "Clarity and focus",
            kits: [1, 2, 4, 6]
          }
        ]
      },
      {
        id: "q4",
        text: "What do you want to cultivate right now?",
        subtitle: "Select up to 2 options",
        maxSelections: 2,
        options: [
          {
            id: "q4_a",
            text: "Living with more lightness",
            kits: [1, 6, 7]
          },
          {
            id: "q4_b",
            text: "Listening to myself and trusting my decisions",
            kits: [6]
          },
          {
            id: "q4_c",
            text: "Putting myself first without guilt",
            kits: [6]
          },
          {
            id: "q4_d",
            text: "Acting with more intention",
            kits: [3, 4, 6]
          },
          {
            id: "q4_e",
            text: "Closing cycles and moving forward lightly",
            kits: [2, 5]
          },
          {
            id: "q4_f",
            text: "Reconnecting with what is essential in me",
            kits: [4]
          }
        ]
      }
    ]
  };

  // Short, explanatory copy for the results screen (no verbatim echoing of answers)
  const KIT_EXPLANATIONS = {
    1: "This kit is designed for moments when your mind feels overloaded and it’s hard to slow down. The sequence helps reduce mental noise, create structure, and return to a steadier internal pace.",
    2: "This supports deep fatigue — the kind that lingers even after rest. It focuses on downshifting your system, restoring steadiness, and creating space for real recovery through simple, guided rituals.",
    3: "This kit is for when you need protection and grounding. The rituals help you settle, strengthen boundaries, and reconnect with a sense of safety in your body and environment.",
    4: "This kit is designed for disconnection — when life keeps moving, but you feel far from yourself. The sequence supports presence, emotional clarity, and reconnection with what matters.",
    5: "This supports closure and emotional release. It offers a gentle structure to process what’s unresolved, soften internal tension, and move forward with less weight.",
    6: "This kit is for rebuilding self-trust and confidence. The rituals help you return to your own center, reinforce inner authority, and strengthen your ability to choose yourself.",
    7: "This kit supports lightness and renewal — especially after periods of heaviness. The sequence helps lift your internal atmosphere and create room for joy, creativity, and expansion."
  };

  // Main KitFinder class
  class KitFinder {
    constructor(container) {
      this.container = container;
      this.currentQuestion = 0;
      this.answers = {};
      this.scores = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
      this.config = window.kitFinderConfig || {};
      this.userEmail = null;

      this.init();
    }

    init() {
      this.cacheElements();
      this.bindEvents();
    }

    cacheElements() {
      // Screens
      this.introScreen = this.container.querySelector('[data-screen="intro"]');
      this.quizScreen = this.container.querySelector('[data-screen="quiz"]');
      this.emailScreen = this.container.querySelector('[data-screen="email"]');
      this.resultsScreen = this.container.querySelector('[data-screen="results"]');

      // Quiz elements
      this.questionContainer = this.container.querySelector('[data-question-container]');
      this.progressFill = this.container.querySelector('[data-progress-fill]');
      this.progressText = this.container.querySelector('[data-progress-text]');
      this.validationMsg = this.container.querySelector('[data-validation-msg]');

      // Buttons
      this.startBtn = this.container.querySelector('[data-start-quiz]');
      this.prevBtn = this.container.querySelector('[data-prev-btn]');
      this.nextBtn = this.container.querySelector('[data-next-btn]');
      this.submitBtn = this.container.querySelector('[data-submit-btn]');
      this.restartBtn = this.container.querySelector('[data-restart-quiz]');

      // Ensure quiz buttons use Dawn button styles
      if (this.startBtn) this.startBtn.classList.add('button', 'button--primary');
      if (this.nextBtn) this.nextBtn.classList.add('button', 'button--primary');
      if (this.submitBtn) this.submitBtn.classList.add('button', 'button--primary');
      if (this.prevBtn) this.prevBtn.classList.add('button', 'button--secondary');
      if (this.restartBtn) this.restartBtn.classList.add('button', 'button--secondary');

      // Email elements
      if (this.config.enableEmailStep) {
        this.emailForm = this.container.querySelector('[data-email-form]');
        this.emailInput = this.container.querySelector('[data-email-input]');
        this.emailError = this.container.querySelector('[data-email-error]');
        this.skipEmailBtn = this.container.querySelector('[data-skip-email]');
      }

      // Results
      this.resultsContent = this.container.querySelector('[data-results-content]');
    }

    bindEvents() {
      this.startBtn?.addEventListener('click', () => this.startQuiz());
      this.prevBtn?.addEventListener('click', () => this.prevQuestion());
      this.nextBtn?.addEventListener('click', () => this.nextQuestion());
      this.submitBtn?.addEventListener('click', () => this.handleQuizSubmit());
      this.restartBtn?.addEventListener('click', () => this.restartQuiz());

      // Email form events
      if (this.config.enableEmailStep) {
        this.emailForm?.addEventListener('submit', (e) => this.handleEmailSubmit(e));
        this.skipEmailBtn?.addEventListener('click', () => this.skipEmail());
      }
    }

    startQuiz() {
      this.showScreen('quiz');
      this.renderQuestion();
      this.focusScreenHeading();
    }

    showScreen(screenName) {
      const screens = this.container.querySelectorAll('.kit-finder-screen');
      screens.forEach(screen => screen.classList.remove('active'));

      const targetScreen = this.container.querySelector(`[data-screen="${screenName}"]`);
      if (targetScreen) {
        targetScreen.classList.add('active');
      }
    }

    focusScreenHeading() {
      // Focus first heading in active screen for accessibility
      const activeScreen = this.container.querySelector('.kit-finder-screen.active');
      const heading = activeScreen?.querySelector('h2, h3');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    renderQuestion() {
      const question = QUIZ_DATA.questions[this.currentQuestion];
      if (!question) return;

      // Update progress
      const progress = ((this.currentQuestion + 1) / QUIZ_DATA.questions.length) * 100;
      this.progressFill.style.width = `${progress}%`;
      this.progressText.textContent = `Question ${this.currentQuestion + 1} of ${QUIZ_DATA.questions.length}`;

      // Render question
      const questionHTML = `
        <div class="kit-finder-question" data-question-id="${question.id}">
          <h3 class="kit-finder-question-text">${question.text}</h3>
          <p class="kit-finder-question-subtitle">${question.subtitle}</p>

          <div class="kit-finder-options" role="group" aria-label="${this.escapeHtml(question.text)}">
            ${question.options.map(option => `
              <label class="kit-finder-option">
                <input
                  type="checkbox"
                  name="${question.id}"
                  value="${option.id}"
                  id="${option.id}"
                  ${this.answers[question.id]?.includes(option.id) ? 'checked' : ''}
                  data-max-selections="${question.maxSelections}"
                >
                <span class="kit-finder-option-checkmark" aria-hidden="true"></span>
                <span class="kit-finder-option-text">${this.escapeHtml(option.text)}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;

      this.questionContainer.innerHTML = questionHTML;

      // Bind checkbox events
      this.bindCheckboxEvents(question);

      // Update buttons
      this.updateNavButtons();

      // Focus question heading
      this.focusScreenHeading();
    }

    bindCheckboxEvents(question) {
      const checkboxes = this.questionContainer.querySelectorAll('input[type="checkbox"]');
      const maxSelections = question.maxSelections;

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          const checked = Array.from(checkboxes).filter(cb => cb.checked);

          // Validate selection limit
          if (checked.length > maxSelections) {
            e.target.checked = false;
            const message = maxSelections === 1
              ? 'Please select 1 option.'
              : `Select up to ${maxSelections} options.`;
            this.showValidation(message);
            return;
          }

          this.hideValidation();

          // Save answer
          this.answers[question.id] = checked.map(cb => cb.value);

          // Update nav buttons
          this.updateNavButtons();
        });
      });
    }

    showValidation(message) {
      this.validationMsg.textContent = message;
      this.validationMsg.style.display = 'block';

      // Auto-hide after 3s
      setTimeout(() => this.hideValidation(), 3000);
    }

    hideValidation() {
      this.validationMsg.style.display = 'none';
    }

    updateNavButtons() {
      const currentAnswers = this.answers[QUIZ_DATA.questions[this.currentQuestion].id] || [];
      const hasAnswers = currentAnswers.length > 0;

      // Prev button
      this.prevBtn.disabled = this.currentQuestion === 0;

      // Next/Submit button
      const isLastQuestion = this.currentQuestion === QUIZ_DATA.questions.length - 1;

      if (isLastQuestion) {
        this.nextBtn.style.display = 'none';
        this.submitBtn.style.display = 'inline-block';
        this.submitBtn.disabled = !hasAnswers;
      } else {
        this.nextBtn.style.display = 'inline-block';
        this.submitBtn.style.display = 'none';
        this.nextBtn.disabled = !hasAnswers;
      }
    }

    prevQuestion() {
      if (this.currentQuestion > 0) {
        this.currentQuestion--;
        this.renderQuestion();
        this.hideValidation();
      }
    }

    nextQuestion() {
      const currentAnswers = this.answers[QUIZ_DATA.questions[this.currentQuestion].id] || [];

      if (currentAnswers.length === 0) {
        this.showValidation('Please select at least one option to continue.');
        return;
      }

      if (this.currentQuestion < QUIZ_DATA.questions.length - 1) {
        this.currentQuestion++;
        this.renderQuestion();
        this.hideValidation();
      }
    }

    handleQuizSubmit() {
      const currentAnswers = this.answers[QUIZ_DATA.questions[this.currentQuestion].id] || [];

      if (currentAnswers.length === 0) {
        this.showValidation('Please select at least one option to continue.');
        return;
      }

      // If email step is enabled, show email screen; otherwise show results
      if (this.config.enableEmailStep && this.emailScreen) {
        this.showScreen('email');
        this.focusScreenHeading();
      } else {
        this.showResults();
      }
    }

    handleEmailSubmit(e) {
      e.preventDefault();

      const email = this.emailInput.value.trim();

      // If email is provided, validate it
      if (email) {
        if (!this.isValidEmail(email)) {
          this.showEmailError('Please enter a valid email address');
          return;
        }
        this.userEmail = email;
        this.hideEmailError();
      }

      // Proceed to results (email is optional)
      this.showResults();
    }

    skipEmail() {
      this.userEmail = null;
      this.showResults();
    }

    isValidEmail(email) {
      // Basic email validation
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    }

    showEmailError(message) {
      this.emailError.textContent = message;
      this.emailError.style.display = 'block';
      this.emailInput.setAttribute('aria-invalid', 'true');
    }

    hideEmailError() {
      this.emailError.style.display = 'none';
      this.emailInput.removeAttribute('aria-invalid');
    }

    calculateScores() {
      // Reset scores
      this.scores = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };

      // Calculate scores based on answers
      QUIZ_DATA.questions.forEach(question => {
        const selectedOptions = this.answers[question.id] || [];

        selectedOptions.forEach(selectedId => {
          const option = question.options.find(opt => opt.id === selectedId);
          if (option) {
            option.kits.forEach(kitNum => {
              this.scores[kitNum] = (this.scores[kitNum] || 0) + 1;
            });
          }
        });
      });

      return this.scores;
    }

    getRecommendedKit() {
      this.calculateScores();

      // Find max score
      const maxScore = Math.max(...Object.values(this.scores));

      // Get all kits with max score
      const topKits = Object.entries(this.scores)
        .filter(([_, score]) => score === maxScore)
        .map(([kitNum, _]) => parseInt(kitNum));

      // Tie-break: lowest kit number wins
      const recommendedKit = Math.min(...topKits);

      return {
        kitNumber: recommendedKit,
        kitName: this.config.kitNames[recommendedKit],
        score: maxScore,
        allScores: { ...this.scores }
      };
    }

    getWhyReasons(kitNumber) {
      // Return a single explanatory paragraph per kit.
      // We keep the method name to avoid changing too many call sites.
      const text = KIT_EXPLANATIONS[kitNumber];
      if (text) return [text];
      return ["This recommendation reflects the support that best matches what you shared."];
    }

    showResults() {
      const result = this.getRecommendedKit();
      const reasons = this.getWhyReasons(result.kitNumber);
      const KIT_PRODUCT_URLS = {
        1: 'https://justyouforyou.com/products/ju4u-k1',
        2: 'https://justyouforyou.com/products/ju4u-k2',
        3: 'https://justyouforyou.com/products/ju4u-k3',
        4: 'https://justyouforyou.com/products/ju4u-k4',
        5: 'https://justyouforyou.com/products/ju4u-k5',
        6: 'https://justyouforyou.com/products/ju4u-k6',
        7: 'https://justyouforyou.com/products/ju4u-k7'
      };
      const configuredUrl = this.config.kitUrls[result.kitNumber];
      const kitUrl = (configuredUrl && configuredUrl !== '#')
        ? configuredUrl
        : KIT_PRODUCT_URLS[result.kitNumber] || '/collections';

      // Save to localStorage
      this.saveResult(result);

      // Render results
      const resultsHTML = `
        <div class="kit-finder-result-card">
          <div class="kit-finder-result-header">
            <p class="kit-finder-eyebrow">Your recommended kit</p>
            <h2 class="kit-finder-result-kit">Kit ${result.kitNumber} — ${this.escapeHtml(result.kitName)}</h2>
          </div>

          <div class="kit-finder-result-why">
            <h3 class="kit-finder-section-title">Why this kit may support you now</h3>
            <p class="kit-finder-muted">${this.escapeHtml(reasons[0] || '')}</p>
          </div>

          <div class="kit-finder-result-cta">
            <a href="${this.escapeHtml(kitUrl)}" class="button button--primary">
              ${this.escapeHtml(this.config.ctaText || 'View kit')}
            </a>
          </div>
        </div>
      `;

      this.resultsContent.innerHTML = resultsHTML;
      this.showScreen('results');
      this.focusScreenHeading();
    }

    saveResult(result) {
      const saveData = {
        kitNumber: result.kitNumber,
        kitName: result.kitName,
        scoreBreakdown: result.allScores,
        answers: this.answers,
        email: this.userEmail || null,
        timestamp: new Date().toISOString()
      };

      try {
        localStorage.setItem('ju4u_kit_reco', JSON.stringify(saveData));
      } catch (e) {
        // Silent fail for localStorage (privacy mode, quota exceeded, etc.)
      }
    }

    restartQuiz() {
      this.currentQuestion = 0;
      this.answers = {};
      this.scores = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
      this.userEmail = null;

      // Reset email form if exists
      if (this.emailForm) {
        this.emailForm.reset();
        this.hideEmailError();
      }

      this.showScreen('intro');
      this.hideValidation();
      this.focusScreenHeading();
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize when DOM is ready
  function init() {
    const containers = document.querySelectorAll('[data-kit-finder]');
    containers.forEach(container => {
      new KitFinder(container);
    });
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
